import { useState, useEffect, memo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, Package, Stethoscope, Save, Send, Loader2, Users, Clock, Calendar, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Time Input Component with local state to prevent re-renders
const TimeInput = memo(({ value, onChange }) => {
  const [localValue, setLocalValue] = useState(value);
  
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  return (
    <div className="space-y-2">
      <Label>Heure d'envoi (UTC+1 Tunisie)</Label>
      <input
        type="time"
        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={(e) => {
          if (e.target.value) {
            onChange(e.target.value);
          }
        }}
      />
    </div>
  );
});

// Alert Panel Component - extracted and memoized
const AlertPanel = memo(({ 
  title, 
  description, 
  icon: Icon, 
  iconColor,
  bgColor,
  borderColor,
  config, 
  onConfigChange,
  status, 
  saving, 
  testing, 
  onSubmit, 
  onTest, 
  onResetLastSent,
  orgUsers,
  extraFields,
  templates,
  templateType
}) => {
  const toggleRecipient = (userId) => {
    const newRecipients = config.recipient_user_ids.includes(userId)
      ? config.recipient_user_ids.filter(id => id !== userId)
      : [...config.recipient_user_ids, userId];
    onConfigChange({ ...config, recipient_user_ids: newRecipients });
  };

  return (
    <Card className="border-0 shadow-md">
      <CardHeader>
        <CardTitle className="text-xl flex items-center space-x-2">
          <Icon className={`w-5 h-5 ${iconColor}`} />
          <span>{title}</span>
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Status Display */}
        {status && status.status === 'active' && (
          <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="font-medium text-green-700">Scheduler actif</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-green-600">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Heure serveur: </span>
                <span className="font-medium">{status.current_server_time}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Prochaine exécution: </span>
                <span className="font-medium">{status.next_run}</span>
              </div>
              {status.last_sent && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-500 dark:text-gray-400">Dernier envoi: </span>
                  <span className="font-medium">{new Date(status.last_sent).toLocaleString('fr-FR')}</span>
                  <button
                    type="button"
                    onClick={onResetLastSent}
                    className="text-xs text-amber-600 hover:text-amber-700 underline ml-2"
                  >
                    (Réinitialiser)
                  </button>
                </div>
              )}
              <div>
                <span className="text-gray-500 dark:text-gray-400">Destinataires: </span>
                <span className="font-medium">{status.recipients_count} utilisateur(s)</span>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className={`flex items-center justify-between p-4 ${bgColor} rounded-lg border ${borderColor}`}>
            <div className="space-y-0.5">
              <Label className="text-base font-medium">Activer les alertes automatiques</Label>
              <p className="text-sm text-gray-500 dark:text-gray-400">Recevoir des notifications par email</p>
            </div>
            <button
              type="button"
              onClick={() => onConfigChange({ ...config, enabled: !config.enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                config.enabled ? 'bg-slate-50 dark:bg-slate-900/300' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                config.enabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {config.enabled && (
            <>
              {/* Frequency Settings */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Fréquence d'envoi</Label>
                  <Select
                    value={config.frequency}
                    onValueChange={(value) => onConfigChange({ ...config, frequency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">📅 Tous les jours</SelectItem>
                      <SelectItem value="weekly">📆 Toutes les semaines</SelectItem>
                      <SelectItem value="monthly">🗓️ Tous les mois</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <TimeInput 
                  value={config.time_of_day} 
                  onChange={(val) => onConfigChange({ ...config, time_of_day: val })}
                />
              </div>

              {/* Weekly Day Selection */}
              {config.frequency === 'weekly' && (
                <div className="space-y-2">
                  <Label>Jour de la semaine</Label>
                  <Select
                    value={config.custom_day?.toString()}
                    onValueChange={(value) => onConfigChange({ ...config, custom_day: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Lundi</SelectItem>
                      <SelectItem value="2">Mardi</SelectItem>
                      <SelectItem value="3">Mercredi</SelectItem>
                      <SelectItem value="4">Jeudi</SelectItem>
                      <SelectItem value="5">Vendredi</SelectItem>
                      <SelectItem value="6">Samedi</SelectItem>
                      <SelectItem value="7">Dimanche</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Monthly Day Selection */}
              {config.frequency === 'monthly' && (
                <div className="space-y-2">
                  <Label>Jour du mois</Label>
                  <Select
                    value={config.custom_day?.toString()}
                    onValueChange={(value) => onConfigChange({ ...config, custom_day: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(28)].map((_, i) => (
                        <SelectItem key={i + 1} value={(i + 1).toString()}>
                          {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Extra Fields (specific to each alert type) */}
              {extraFields}

              {/* Template Selector */}
              {templates && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Template Email
                  </Label>
                  <Select
                    value={config.template_id || "default"}
                    onValueChange={(value) => onConfigChange({ ...config, template_id: value === "default" ? null : value })}
                  >
                    <SelectTrigger data-testid={`template-select-${templateType}`}>
                      <SelectValue placeholder="Template par défaut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">📧 Template par défaut</SelectItem>
                      {templates.filter(t => t.type === templateType).map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          🎨 {t.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Recipients */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Destinataires
                </Label>
                <div className="p-4 bg-gray-50 dark:bg-gray-950 rounded-lg border max-h-60 overflow-y-auto space-y-2">
                  {orgUsers.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Aucun utilisateur dans l'organisation</p>
                  ) : (
                    orgUsers.map((u) => (
                      <label key={u.id} className="flex items-center gap-3 p-2 hover:bg-white dark:bg-gray-950 rounded cursor-pointer">
                        <Checkbox
                          checked={config.recipient_user_ids.includes(u.id)}
                          onCheckedChange={() => toggleRecipient(u.id)}
                        />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{u.nom || u.email}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {u.role}
                        </span>
                      </label>
                    ))
                  )}
                </div>
                {config.recipient_user_ids.length === 0 && (
                  <p className="text-xs text-amber-600">⚠️ Sélectionnez au moins un destinataire</p>
                )}
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="submit"
              disabled={saving}
              className="bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            
            {config.enabled && config.recipient_user_ids.length > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={onTest}
                disabled={testing}
                className="border-gray-200 dark:border-gray-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:bg-slate-900/30"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {testing ? 'Envoi...' : 'Tester'}
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
});

const Alertes = ({ user }) => {
  const isAdmin = user?.role === 'admin';
  const [orgUsers, setOrgUsers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // Stock Alert State
  const [stockConfig, setStockConfig] = useState({
    enabled: false,
    frequency: 'daily',
    custom_day: 1,
    custom_date: '',
    time_of_day: '08:00',
    min_alerts: 0,
    recipient_user_ids: [],
    template_id: null,
  });
  const [stockStatus, setStockStatus] = useState(null);
  const [savingStock, setSavingStock] = useState(false);
  const [testingStock, setTestingStock] = useState(false);

  // Health Alert State
  const [healthConfig, setHealthConfig] = useState({
    enabled: false,
    frequency: 'daily',
    custom_day: 1,
    custom_date: '',
    time_of_day: '08:00',
    alert_delays: [],
    recipient_user_ids: [],
    template_id: null,
  });
  const [healthStatus, setHealthStatus] = useState(null);
  const [savingHealth, setSavingHealth] = useState(false);
  const [testingHealth, setTestingHealth] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchOrgUsers();
      fetchStockConfig();
      fetchHealthConfig();
      fetchEmailTemplates();
    }
  }, [isAdmin]);

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email-templates/`);
      setEmailTemplates(response.data);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const fetchOrgUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setOrgUsers(response.data.filter(u => u.is_active !== false));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // ========== STOCK ALERTS ==========
  const fetchStockConfig = async () => {
    try {
      const response = await axios.get(`${API}/stock-alert-config`);
      setStockConfig({
        enabled: response.data.enabled || false,
        frequency: response.data.frequency || 'daily',
        custom_day: response.data.custom_day || 1,
        custom_date: response.data.custom_date || '',
        time_of_day: response.data.time_of_day || '08:00',
        min_alerts: response.data.min_alerts ?? 0,
        recipient_user_ids: response.data.recipient_user_ids || [],
        template_id: response.data.template_id || null,
      });
      fetchStockStatus();
    } catch (error) {
      console.error('Error fetching stock alert config:', error);
    }
  };

  const fetchStockStatus = async () => {
    try {
      const response = await axios.get(`${API}/stock-alert-config/status`);
      setStockStatus(response.data);
    } catch (error) {
      console.error('Error fetching stock alert status:', error);
    }
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    setSavingStock(true);
    try {
      await axios.post(`${API}/stock-alert-config`, stockConfig);
      toast.success('Configuration des alertes stock enregistrée');
      fetchStockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingStock(false);
    }
  };

  const handleTestStock = async () => {
    setTestingStock(true);
    try {
      const response = await axios.post(`${API}/stock-alert-config/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTestingStock(false);
    }
  };

  const handleResetStockLastSent = async () => {
    try {
      const response = await axios.post(`${API}/stock-alert-config/reset-last-sent`);
      toast.success(response.data.message);
      fetchStockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  // ========== HEALTH ALERTS ==========
  const fetchHealthConfig = async () => {
    try {
      const response = await axios.get(`${API}/health-alert-config`);
      setHealthConfig({
        enabled: response.data.enabled || false,
        frequency: response.data.frequency || 'daily',
        custom_day: response.data.custom_day || 1,
        custom_date: response.data.custom_date || '',
        time_of_day: response.data.time_of_day || '08:00',
        alert_delays: response.data.alert_delays || [],
        recipient_user_ids: response.data.recipient_user_ids || [],
        template_id: response.data.template_id || null,
      });
      fetchHealthStatus();
    } catch (error) {
      console.error('Error fetching health alert config:', error);
    }
  };

  const fetchHealthStatus = async () => {
    try {
      const response = await axios.get(`${API}/health-alert-config/status`);
      setHealthStatus(response.data);
    } catch (error) {
      console.error('Error fetching health alert status:', error);
    }
  };

  const handleHealthSubmit = async (e) => {
    e.preventDefault();
    setSavingHealth(true);
    try {
      await axios.post(`${API}/health-alert-config`, healthConfig);
      toast.success('Configuration des alertes santé enregistrée');
      fetchHealthStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingHealth(false);
    }
  };

  const handleTestHealth = async () => {
    setTestingHealth(true);
    try {
      const response = await axios.post(`${API}/health-alert-config/test`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du test');
    } finally {
      setTestingHealth(false);
    }
  };

  const handleResetHealthLastSent = async () => {
    try {
      const response = await axios.post(`${API}/health-alert-config/reset-last-sent`);
      toast.success(response.data.message);
      fetchHealthStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const toggleHealthDelay = (delay) => {
    setHealthConfig(prev => {
      const newDelays = prev.alert_delays.includes(delay)
        ? prev.alert_delays.filter(d => d !== delay)
        : [...prev.alert_delays, delay];
      return { ...prev, alert_delays: newDelays };
    });
  };

  if (!isAdmin) {
    return (
      <div className="p-6">
        <Card className="border-0 shadow-md">
          <CardContent className="pt-6">
            <p className="text-center text-gray-500 dark:text-gray-400">
              Seul l'administrateur peut configurer les alertes.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alertes-page">
      {/* En-tête harmonisé */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Alertes Automatiques</h1>
        </div>
      </div>

      {/* Stock Alerts Panel */}
      <AlertPanel
        title="Alertes Stock"
        description="Recevez des alertes quand vos stocks alimentaires sont bas"
        icon={Package}
        iconColor="text-amber-600"
        bgColor="bg-amber-50"
        borderColor="border-amber-200"
        config={stockConfig}
        onConfigChange={setStockConfig}
        status={stockStatus}
        saving={savingStock}
        testing={testingStock}
        onSubmit={handleStockSubmit}
        onTest={handleTestStock}
        onResetLastSent={handleResetStockLastSent}
        orgUsers={orgUsers}
        templates={emailTemplates}
        templateType="stock_alert"
        extraFields={
          <div className="space-y-2">
            <Label>Seuil de criticité (nombre minimum d'alertes)</Label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="0"
                className="flex h-10 w-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={stockConfig.min_alerts}
                onChange={(e) => setStockConfig(prev => ({ ...prev, min_alerts: parseInt(e.target.value) || 0 }))}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {stockConfig.min_alerts === 0 
                  ? "Envoyer même si aucune alerte (rapport complet)"
                  : `Envoyer seulement si ${stockConfig.min_alerts}+ produit(s) en alerte`
                }
              </span>
            </div>
          </div>
        }
      />

      {/* Health Alerts Panel */}
      <AlertPanel
        title="Alertes Vaccinations & Santé"
        description="Recevez des rappels pour les prochains RDV vétérinaires de vos moutons"
        icon={Stethoscope}
        iconColor="text-blue-600"
        bgColor="bg-blue-50"
        borderColor="border-blue-200"
        config={healthConfig}
        onConfigChange={setHealthConfig}
        status={healthStatus}
        saving={savingHealth}
        testing={testingHealth}
        onSubmit={handleHealthSubmit}
        onTest={handleTestHealth}
        onResetLastSent={handleResetHealthLastSent}
        orgUsers={orgUsers}
        templates={emailTemplates}
        templateType="health_alert"
        extraFields={
          <div className="space-y-3">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Délais d'alerte (alerter X jours avant le RDV)
            </Label>
            <div className="flex flex-wrap gap-3">
              {[
                { value: '1_day', label: '1 jour avant', color: 'red' },
                { value: '1_week', label: '1 semaine avant', color: 'orange' },
                { value: '1_month', label: '1 mois avant', color: 'blue' },
              ].map(delay => (
                <label
                  key={delay.value}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-all ${
                    healthConfig.alert_delays.includes(delay.value)
                      ? delay.color === 'red' 
                        ? 'bg-red-100 border-red-300 text-red-700'
                        : delay.color === 'orange'
                        ? 'bg-orange-100 border-orange-300 text-orange-700'
                        : 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-gray-50 border-gray-200 dark:border-gray-800 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Checkbox
                    checked={healthConfig.alert_delays.includes(delay.value)}
                    onCheckedChange={() => toggleHealthDelay(delay.value)}
                  />
                  <Clock className="w-4 h-4" />
                  <span className="font-medium">{delay.label}</span>
                </label>
              ))}
            </div>
            {healthConfig.alert_delays.length === 0 && (
              <p className="text-xs text-amber-600">⚠️ Sélectionnez au moins un délai d'alerte</p>
            )}
          </div>
        }
      />
    </div>
  );
};

export default Alertes;
